import { useEffect, useRef, useState } from 'react';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import askAiAPI from './askAiApi.js';

const AskAiModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const [messages, setMessages] = useState([{ role: 'assistant', content: t('ask_ai_initial_message') }]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSendHovered, setIsSendHovered] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isLoading]);

    const handleSend = async () => {
        const trimmed = inputValue.trim();
        if (!trimmed || isLoading) return;

        const userMsg = { role: 'user', content: trimmed };
        setMessages((prev) => [...prev, userMsg]);
        setInputValue('');
        setIsLoading(true);

        try {
            const response = await askAiAPI.search(trimmed);
            const assistantMsg = {
                role: 'assistant',
                content: response?.data?.analysis ?? t('ask_ai_fallback_message'),
            };
            setMessages((prev) => [...prev, assistantMsg]);
        } catch {
            const errorMsg = {
                role: 'assistant',
                content: t('ask_ai_error_message'),
            };
            setMessages((prev) => [...prev, errorMsg]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSend();
        }
    };

    return (
        <div
            className={`fixed bottom-32 right-6 origin-bottom-right w-[450px] rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden transition-all duration-200 ease-out ${
                isOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-90 pointer-events-none'
            }`}
            style={{ zIndex: 100000000, maxHeight: '650px' }}
        >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-5 h-5 text-gray-500" />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-900 leading-tight">{t('ask_ai_title')}</span>
                        <span className="text-xs text-gray-400">{t('ask_ai_subtitle')}</span>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label={t('ask_ai_close')}
                >
                    <X size={20} />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-[380px] max-h-[480px]">
                {messages.map((msg, idx) => (
                    <div
                        key={idx}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                        <div
                            className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm leading-relaxed whitespace-pre-wrap ${msg.role === 'user'
                                ? 'rounded-br-sm'
                                : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                                }`}
                            style={msg.role === 'user' ? {
                                backgroundColor: 'var(--primary-color, #2F4E70)',
                                color: 'var(--box-border-color, #e0e0e0)',
                            } : undefined}
                        >
                            {msg.content}
                        </div>
                    </div>
                ))}

                {isLoading && (
                    <div className="flex justify-start">
                        <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1">
                            <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0ms]" />
                            <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:150ms]" />
                            <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:300ms]" />
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            <div className="px-4 py-3 border-t border-gray-100 flex items-center gap-2">
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => setIsInputFocused(false)}
                    placeholder={t('ask_ai_input_placeholder')}
                    disabled={isLoading}
                    className="flex-1 bg-gray-50 rounded-full px-4 py-2 text-sm outline-none border transition-colors placeholder:text-gray-400 disabled:opacity-60"
                    style={{ borderColor: isInputFocused ? 'var(--primary-color, #1a1a2e)' : '#e5e7eb' }}
                />
                <button
                    onClick={handleSend}
                    disabled={!inputValue.trim() || isLoading}
                    onMouseEnter={() => setIsSendHovered(true)}
                    onMouseLeave={() => setIsSendHovered(false)}
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                    style={{
                        backgroundColor: isSendHovered ? 'var(--hover1-color, #0f3460)' : 'var(--primary-color, #2F4E70)',
                        color: isSendHovered ? 'var(--box-border-hover-color, #ffffff)' : 'var(--box-border-color, #e0e0e0)',
                    }}
                    aria-label={t('ask_ai_send')}
                >
                    <ArrowRight size={18} />
                </button>
            </div>

            <div className='p-5 pt-2'>
                <p className='text-[10px] text-center text-gray-400'>{t('ask_ai_disclaimer')}</p>
            </div>
        </div>
    );
};

export default AskAiModal;
